"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.VideoController = void 0;
const analytics_1 = require("@streem/analytics");
const sdk_types_1 = require("@streem/sdk-types");
const toolbox_1 = require("@streem/toolbox");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const streem_sdk_protobuf_1 = require("streem-sdk-protobuf");
const logger_1 = require("../../../util/logger");
const filter_null_1 = require("../../../util/rx/filter_null");
const take_first_emitted_1 = require("../../../util/rx/take_first_emitted");
const connected_strategy_1 = require("./connected_strategy");
const track_constraints_1 = require("../../../util/track_constraints");
const MIN_TIME_BETWEEN_TRACK_RESTARTS_MS = 10 * 1000; // 10 seconds
class VideoController {
    constructor(config) {
        this.config = config;
        this.log = logger_1.logger.extend('sdk:video_controller');
        this.destroyed = new rxjs_1.Subject();
        // We use this to track the state we use to pass to the video track since it is not guaranteed to
        // exist when we call the constructor and connect()
        this.backgroundedYn = false;
        this.id = config.id;
        this.ownerIsLocalUser = config.owner.isLocalUser;
        this.ownerId = config.owner.id;
        this.videoTrackWallItem = this.config.wallItem.pipe((0, operators_1.shareReplay)(1));
        this.lastTrackRestart = Date.now();
        /**
         * Listen for the latest video track
         * and set it as a property on this class.
         */
        this.config.track
            .pipe((0, operators_1.takeUntil)(this.destroyed))
            .subscribe((v) => {
            this.videoTrack = v;
            // Handle the condition where `connect()` is called before the remote track exists
            (v === null || v === void 0 ? void 0 : v.setBackgrounded) && v.setBackgrounded(this.backgroundedYn);
        });
    }
    connect() {
        this.cameraSubject = this.videoTrackWallItem.pipe(
        // Using `0` (World) is Replicating Protobuf behavior/response with missing cameraSubject
        (0, operators_1.map)(item => {
            var _a;
            const cameraSubject = (_a = item === null || item === void 0 ? void 0 : item.cameraSubject) !== null && _a !== void 0 ? _a : streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_WORLD;
            // update internally so track restart can always reference the current value
            this._cameraSubject = cameraSubject;
            return cameraSubject;
        }), (0, filter_null_1.filterNull)(), (0, operators_1.shareReplay)(1));
        this.muted = this.buildMutedStatus(this.videoTrackWallItem);
        this.enabled = this.buildEnabledStatus(this.config.track);
        this.started = this.buildStartedStatus(this.config.track);
        this.connected = this.buildConnectedStatus(this.config.track);
        this.backgrounded = this.buildBackgroundedStatus(this.videoTrackWallItem);
        this.backgrounded.subscribe(backgrounded => {
            var _a;
            this.backgroundedYn = backgrounded;
            ((_a = this.videoTrack) === null || _a === void 0 ? void 0 : _a.setBackgrounded) &&
                this.videoTrack.setBackgrounded(this.backgroundedYn);
        });
        this.paused = this.buildPausedStatus(this.config.track);
        this.updateVideoTrackWallItemOnTrackUpdates();
    }
    destroy() {
        this.destroyed.next(undefined);
    }
    setConnectedStrategy(strategy) {
        this.connectedStrategy = strategy;
    }
    startRenderingVideoFrames(callback) {
        let stopFrameScheduler;
        const connectedSubscription = this.connected.subscribe({
            next: value => {
                if (stopFrameScheduler) {
                    stopFrameScheduler();
                    stopFrameScheduler = undefined;
                }
                if (value) {
                    (0, toolbox_1.invariant)(this.videoTrack, 'Missing internal videoTrack');
                    stopFrameScheduler = this.videoTrack.scheduleRenderingCallback(callback);
                }
            },
        });
        return () => {
            if (connectedSubscription) {
                connectedSubscription.unsubscribe();
            }
            if (stopFrameScheduler) {
                stopFrameScheduler();
            }
        };
    }
    setEnabled(enabled) {
        if (this.ownerIsLocalUser && this.videoTrack && (0, sdk_types_1.isLocalVideoTrack)(this.videoTrack)) {
            this.videoTrack.setEnabled(enabled);
            (0, analytics_1.recordCameraStatusSet)(enabled, this.config.roomId);
            this.log.info(`Track of type: video has been turned ${enabled ? 'ON' : 'OFF'} for participant: ${this.ownerId.toString()}`);
        }
    }
    updateVideoTrackWallItemOnTrackUpdates() {
        (0, rxjs_1.race)(this.destroyed.asObservable())
            .pipe((0, operators_1.take)(1))
            .subscribe(() => {
            if (this.ownerIsLocalUser) {
                this.config.roomWall.delete(this.id);
            }
            this.config.onDestroy(this);
        });
        /**
         * Whenever we update our enabled state, update the VideoTrack wall item for the local user
         * when the user mutes / unmutes their video.
         */
        this.enabled
            .pipe((0, operators_1.withLatestFrom)(this.connected, this.videoTrackWallItem), (0, operators_1.takeUntil)(this.destroyed))
            .subscribe(([enabled, connected, wallItem]) => {
            if (connected && this.ownerIsLocalUser) {
                this.config.roomWall.addOrUpdate(this.config.roomWall.builder.buildTrackWallItem('videoTrack', {
                    name: wallItem === null || wallItem === void 0 ? void 0 : wallItem.name,
                    cameraSubject: wallItem === null || wallItem === void 0 ? void 0 : wallItem.cameraSubject,
                    active: enabled,
                    backgrounded: false,
                }));
            }
        });
    }
    buildEnabledStatus(track) {
        return track.pipe((0, operators_1.switchMap)(track => !track
            ? (0, rxjs_1.of)(false)
            : track.enabled.pipe((0, operators_1.tap)((e) => this.log.info(`Track of type: video has been ${e ? 'enabled' : 'disabled'} for participant: ${this.ownerId.toString()}`)))), (0, operators_1.shareReplay)(1));
    }
    buildMutedStatus(videoTrackWallItem) {
        return videoTrackWallItem.pipe((0, operators_1.map)(item => !(item === null || item === void 0 ? void 0 : item.active)), (0, operators_1.shareReplay)(1));
    }
    buildBackgroundedStatus(videoTrackWallItem) {
        return videoTrackWallItem.pipe((0, operators_1.map)(item => !!(item === null || item === void 0 ? void 0 : item.backgrounded)), (0, operators_1.tap)(backgrounded => this.log.info(`Track of type: video has been ${backgrounded ? 'backgrounded' : 'foregrounded'} for participant: ${this.ownerId.toString()}`)), (0, operators_1.shareReplay)(1));
    }
    buildStartedStatus(track) {
        return track.pipe((0, operators_1.switchMap)(track => !track
            ? (0, rxjs_1.of)(false)
            : track.started.pipe((0, operators_1.tap)((s) => this.log.info(`Track of type: video has ${s ? 'started' : 'stopped'} for participant: ${this.ownerId.toString()}`)))), (0, operators_1.shareReplay)(1));
    }
    buildConnectedStatus(track) {
        if (!this.connectedStrategy) {
            this.connectedStrategy = new connected_strategy_1.DefaultConnectedStrategy(track);
        }
        return this.connectedStrategy.createConnected().pipe((0, operators_1.tap)(connected => this.log.info(`Track of type: video has been ${connected ? 'connected' : 'disconnected'} for participant: ${this.ownerId.toString()}`)), (0, operators_1.shareReplay)(1));
    }
    buildPausedStatus(track) {
        // paused is an optional property on the track.  As of the writing of this code, it is only
        //    used for the remote track in Chime.
        return track.pipe((0, operators_1.switchMap)(track => track && track.paused
            ? track.paused.pipe((0, operators_1.tap)((s) => this.log.info(`Track of type: video has ${s ? 'paused' : 'unpaused'} for participant: ${this.ownerId.toString()}`)))
            : (0, rxjs_1.of)(false)), (0, operators_1.shareReplay)(1));
    }
    setBackgrounded(backgrounded) {
        return __awaiter(this, void 0, void 0, function* () {
            const wallItem = yield (0, take_first_emitted_1.takeFirstEmitted)(this.videoTrackWallItem);
            this.config.roomWall.update(this.config.roomWall.builder.buildTrackWallItem('videoTrack', Object.assign(Object.assign({}, wallItem), { backgrounded })));
            this.log.info(`videoTrack backgrounded value set to backgrounded=${backgrounded} for userId=${this.config.owner.id.toString()}`);
        });
    }
    flipCamera(newSubject) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.ownerIsLocalUser, "Attempted to flip the camera for a video feed the user doesn't own. Did you mean to request the flip?");
            (0, toolbox_1.invariant)((0, sdk_types_1.isLocalVideoTrack)(this.videoTrack), 'Attempted to flip camera for a video track that does not support it');
            const wallItem = yield (0, take_first_emitted_1.takeFirstEmitted)(this.videoTrackWallItem);
            const defaultConstraints = yield (0, track_constraints_1.generateStreemVideoTrackConstraints)(newSubject);
            let constraints = this.videoTrack.getMediaTrackConstraints();
            // prevent old track 'facingMode' constraint value from overriding new constraint 'facingMode' value
            constraints = Object.assign(Object.assign({}, defaultConstraints), { width: (constraints === null || constraints === void 0 ? void 0 : constraints.width) || defaultConstraints.width, height: (constraints === null || constraints === void 0 ? void 0 : constraints.height) || defaultConstraints.height });
            const successful = yield this.videoTrack.restart(constraints);
            if (successful) {
                this.config.roomWall.update(this.config.roomWall.builder.buildTrackWallItem('videoTrack', Object.assign(Object.assign({}, wallItem), { cameraSubject: newSubject })));
            }
            return successful;
        });
    }
    restart(constraints) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.ownerIsLocalUser, 'Attempted to restart a remote video track you do not control. Did you mean to restart a local track instead?');
            (0, toolbox_1.invariant)((0, sdk_types_1.isLocalVideoTrack)(this.videoTrack), 'Attempted to restart a remote video track. Did you mean to call this on the local video track?');
            const defaultConstraints = yield (0, track_constraints_1.generateStreemVideoTrackConstraints)(this._cameraSubject);
            // override default constraints if the existing track has any previously applied constraints
            let activeConstraints = this.videoTrack.getMediaTrackConstraints();
            activeConstraints = Object.assign(Object.assign(Object.assign({}, defaultConstraints), constraints), { width: (_b = (_a = activeConstraints === null || activeConstraints === void 0 ? void 0 : activeConstraints.width) !== null && _a !== void 0 ? _a : constraints === null || constraints === void 0 ? void 0 : constraints.width) !== null && _b !== void 0 ? _b : defaultConstraints.width, height: (_d = (_c = activeConstraints === null || activeConstraints === void 0 ? void 0 : activeConstraints.height) !== null && _c !== void 0 ? _c : constraints === null || constraints === void 0 ? void 0 : constraints.height) !== null && _d !== void 0 ? _d : defaultConstraints.height });
            return yield this.videoTrack.restart(activeConstraints);
        });
    }
    handlePoorQualityRestart() {
        return __awaiter(this, void 0, void 0, function* () {
            // rate-limit the video track restarts.
            if (this.lastTrackRestart >= Date.now() - MIN_TIME_BETWEEN_TRACK_RESTARTS_MS) {
                this.log.warn(`Local video track quality fell below threshold, but restart rate-limit of once per ${MIN_TIME_BETWEEN_TRACK_RESTARTS_MS} ms has been exceeded.`);
                return;
            }
            const successful = yield this.restart();
            this.lastTrackRestart = Date.now();
            if (successful) {
                this.log.info(`Local video track quality fell below threshold and was restarted successfully for participant with userId=${this.ownerId.toString()}`);
            }
            else {
                this.log.warn(`Local video track quality fell below threshold but failed to restart for participant with userId=${this.ownerId.toString()}`);
            }
        });
    }
    handleBackgroundRestart() {
        return __awaiter(this, void 0, void 0, function* () {
            const successful = yield this.restart();
            if (successful) {
                this.log.info(`Local video track was backgrounded and was restarted successfully for participant with userId=${this.ownerId.toString()}`);
            }
            else {
                this.log.warn(`Local video track was backgrounded but failed to restart for participant with userId=${this.ownerId.toString()}`);
            }
        });
    }
}
exports.VideoController = VideoController;
//# sourceMappingURL=video_controllers.js.map